import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
  alpha,
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuList,
  Paper,
  Popper,
  ClickAwayListener,
  MenuItem,
  SvgIcon,
} from '@mui/material';
import { Link, useMatches } from '@tanstack/react-router';
import { getAppConfiguration, Team, trackAnalytics } from '@puffin/app-frame-api';
import { useEffect, useId, useRef, useState } from 'react';
import { navigationSections, type NavigationPath, type NavigationSection } from '../../routes/navigation';

interface AppNavigationProps {
  onNavigate?: () => void;
  sections?: readonly NavigationSection[];
  initiallyExpanded?: boolean;
  collapsed?: boolean;
}

export const AppNavigation = ({ onNavigate, sections = navigationSections, initiallyExpanded = false, collapsed = false }: AppNavigationProps) => {
  const navigationId = useId();
  const isPlatformTeam = getAppConfiguration().team === Team.PLATFORM;
  const activePath = useMatches({
    select: (matches) => [...matches].reverse().find((match) => match.staticData.navigationPath)?.staticData.navigationPath,
  });
  const activeSectionId = navigationSections.find((section) => section.items.some((item) => 'path' in item && item.path === activePath))?.id;
  const [expanded, setExpanded] = useState<Partial<Record<string, boolean>>>(() =>
    initiallyExpanded ? Object.fromEntries(sections.map((section) => [section.id, true])) : activeSectionId === undefined ? {} : { [activeSectionId]: true },
  );

  if (collapsed) {
    return <CollapsedNavigation sections={sections} activePath={activePath} isPlatformTeam={isPlatformTeam} onNavigate={onNavigate} />;
  }

  return (
    <List component="nav" aria-label="Meisterplan Dev Platform navigation" sx={{ px: 1.5, py: 2 }}>
      {sections.map((section) => {
        const isExpanded = expanded[section.id] ?? section.id === activeSectionId;
        return (
          <Box key={section.id} sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => {
                trackAnalytics('NAVIGATION_SECTION_TOGGLED', { section: section.id, expanded: !isExpanded });
                setExpanded((current) => ({ ...current, [section.id]: !isExpanded }));
              }}
              aria-expanded={isExpanded}
              aria-label={section.label}
              aria-controls={`${navigationId}-${section.id}-navigation`}

              sx={{
                borderRadius: 1.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <SvgIcon fontSize="small">
                  <path d={section.icon} />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText primary={section.label} slotProps={{ primary: { sx: { fontWeight: 600 } } }} />
              {isExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            </ListItemButton>

            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List id={`${navigationId}-${section.id}-navigation`} disablePadding>
                {section.items
                  .filter((item) => !item.platformOnly || isPlatformTeam)
                  .map((item) => {
                    const destination = 'path' in item ? item.path : item.externalUrl;
                    const linkProps =
                      'path' in item
                        ? { component: Link, to: item.path }
                        : { component: 'a' as const, href: item.externalUrl, target: '_blank', rel: 'noopener noreferrer' };
                    return (
                      <ListItemButton
                        key={destination}
                        {...linkProps}
                        aria-label={item.label}
                        aria-current={activePath === destination ? 'page' : undefined}
                        onClick={() => {
                          trackAnalytics('NAVIGATION_ITEM_SELECTED', { section: section.id, destination });
                          onNavigate?.();
                        }}
                        selected={activePath === destination}
                        sx={(theme) => ({
                          borderRadius: 1.5,
                          pl: 3,
                          py: 0.75,
                          my: 0.25,
                          '&.Mui-selected': {
                            color: 'primary.dark',
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          },
                        })}
                      >
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit', opacity: 0.6 }}>
                          <SvgIcon fontSize="small">
                            <path d={item.icon} />
                          </SvgIcon>
                        </ListItemIcon>
                        <ListItemText primary={item.label} slotProps={{ primary: { variant: 'body2' } }} />
                        {'externalUrl' in item ? <OpenInNewRoundedIcon fontSize="small" color="action" aria-label="opens in a new tab" /> : null}
                      </ListItemButton>
                    );
                  })}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </List>
  );
};

const CollapsedNavigation = ({
  sections,
  activePath,
  isPlatformTeam,
  onNavigate,
}: {
  sections: readonly NavigationSection[];
  activePath?: NavigationPath;
  isPlatformTeam: boolean;
  onNavigate?: () => void;
}) => {
  const navigationId = useId();
  const navigationElement = useRef<HTMLElement | null>(null);
  const menuElement = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const suppressFocusOpen = useRef(false);
  const [flyout, setFlyout] = useState<{ sectionId: string; anchorEl: HTMLElement; focusItems: boolean } | null>(null);

  const cancelClose = () => clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    cancelClose();
    if (menuElement.current?.contains(document.activeElement)) return;
    closeTimer.current = setTimeout(() => setFlyout(null), 180);
  };
  const closeFlyout = (restoreFocus = false) => {
    cancelClose();
    setFlyout(null);
    if (restoreFocus && flyout) {
      suppressFocusOpen.current = true;
      flyout.anchorEl.focus();
      suppressFocusOpen.current = false;
    }
  };
  const openFlyout = (sectionId: string, anchorEl: HTMLElement, focusItems = false) => {
    cancelClose();
    if (suppressFocusOpen.current) return;
    if (flyout?.sectionId !== sectionId) trackAnalytics('NAVIGATION_SECTION_TOGGLED', { section: sectionId, expanded: true });
    setFlyout({ sectionId, anchorEl, focusItems });
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleBlur = (relatedTarget: EventTarget | null) => {
    if (relatedTarget instanceof Node && (navigationElement.current?.contains(relatedTarget) || menuElement.current?.contains(relatedTarget))) return;
    scheduleClose();
  };

  return (
    <ClickAwayListener onClickAway={() => closeFlyout()}>
      <List
        ref={navigationElement}
        component="nav"
        aria-label="Meisterplan Dev Platform navigation"
        onMouseLeave={scheduleClose}
        onBlur={(event) => handleBlur(event.relatedTarget)}
        sx={{ px: 1.5, py: 2 }}
      >
        {sections.map((section) => {
          const isOpen = flyout?.sectionId === section.id;
          const buttonId = `${navigationId}-${section.id}-button`;
          const menuId = `${navigationId}-${section.id}-menu`;
          const isActive = section.items.some((item) => 'path' in item && item.path === activePath);
          return (
            <Box key={section.id} sx={{ mb: 0.5 }}>
              <ListItemButton
                id={buttonId}
                aria-label={section.label}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-controls={isOpen ? menuId : undefined}
                selected={isActive}
                onMouseEnter={(event) => openFlyout(section.id, event.currentTarget)}
                onFocus={(event) => openFlyout(section.id, event.currentTarget)}
                onKeyDown={(event) => {
                  if (['ArrowRight', 'ArrowDown', 'Enter', ' '].includes(event.key)) {
                    event.preventDefault();
                    openFlyout(section.id, event.currentTarget, true);
                  } else if (event.key === 'Escape') closeFlyout();
                }}
                sx={{ borderRadius: 1.5, justifyContent: 'center', px: 0, minHeight: 48 }}
              >
                <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                  <SvgIcon>
                    <path d={section.icon} />
                  </SvgIcon>
                </ListItemIcon>
              </ListItemButton>
              <Popper
                open={isOpen}
                placement="right-start"
                anchorEl={
                  isOpen
                    ? {
                        contextElement: flyout.anchorEl,
                        getBoundingClientRect: () => {
                          const anchorRect = flyout.anchorEl.getBoundingClientRect();
                          const right =
                            navigationElement.current?.closest('.MuiDrawer-paper')?.getBoundingClientRect().right ??
                            navigationElement.current?.getBoundingClientRect().right ??
                            anchorRect.right;
                          return new DOMRect(right, anchorRect.top, 0, anchorRect.height);
                        },
                      }
                    : null
                }
                sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}
              >
                <Paper
                  ref={menuElement}
                  elevation={8}
                  onMouseEnter={cancelClose}
                  onMouseLeave={scheduleClose}
                  onBlur={(event) => handleBlur(event.relatedTarget)}
                  sx={{ minWidth: 260 }}
                >
                  <MenuList
                    id={menuId}
                    aria-labelledby={buttonId}
                    autoFocusItem={isOpen && flyout.focusItems}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape' || event.key === 'ArrowLeft') {
                        event.preventDefault();
                        event.stopPropagation();
                        closeFlyout(true);
                      }
                    }}
                  >
                    {section.items
                      .filter((item) => !item.platformOnly || isPlatformTeam)
                      .map((item) => {
                        const destination = 'path' in item ? item.path : item.externalUrl;
                        const linkProps =
                          'path' in item
                            ? { component: Link, to: item.path }
                            : { component: 'a' as const, href: item.externalUrl, target: '_blank', rel: 'noopener noreferrer' };
                        return (
                          <MenuItem
                            key={destination}
                            {...linkProps}
                            aria-label={item.label}
                            aria-current={activePath === destination ? 'page' : undefined}
                            selected={activePath === destination}
                            onClick={() => {
                              trackAnalytics('NAVIGATION_ITEM_SELECTED', { section: section.id, destination });
                              closeFlyout();
                              onNavigate?.();
                            }}
                          >
                            <ListItemIcon sx={{ color: 'inherit', opacity: 0.6 }}>
                              <SvgIcon fontSize="small">
                                <path d={item.icon} />
                              </SvgIcon>
                            </ListItemIcon>
                            <ListItemText primary={item.label} slotProps={{ primary: { variant: 'body2' } }} />
                            {'externalUrl' in item && <OpenInNewRoundedIcon fontSize="small" color="action" aria-label="opens in a new tab" />}
                          </MenuItem>
                        );
                      })}
                  </MenuList>
                </Paper>
              </Popper>
            </Box>
          );
        })}
      </List>
    </ClickAwayListener>
  );
};
